import { action, computed, observable, autorun, when } from 'mobx';
import Alert from 'react-s-alert';

import { AppManager, Input, Variables } from 'core';
import FileAppManager from 'apps/file/appManager';
import { ConversionProgressSubscription } from 'apps/file/gql';

import { FormStore } from 'libs/forms/app';
import {
  RFQQuery,
  UpdateRFQMutation,
  UpdateRequestForQuoteStateMutation,
} from 'apps/rfq/gql';
import { RFQModel, RFQApp } from 'apps/rfq/app';

import { clientStore } from 'apps/client/app';
import { ProjectContextModel, ProjectContextApp } from 'apps/context/app';

import { ensureIsGraphqlId } from 'utils/dgraphId';

import QualificationFormAppManager from './qualificationFormAppManager';

const addressConfigForRFQPage = {
  addressForm: {
    fields: ['address'],
    styles: {
      formColumns: 1,
      labelBold: true,
    },
  },
  title: 'Order Details.',
  fields: {
    address: {
      // label: null,
      doNotRenderAddressValue: true,
      doNotDeselectOnDeletion: true,
      placeholder: 'Choose an Address',
      component: 'addresses.AddressChoiceField', // what to do if no addresses have been added - problem with adding an address and selecting it at the same time
      noValidation: true,
      oneColumn: true,
      preventAutoSelectionOfDefaultAddress: true,
    },
  },
};
export default class RFQAppManager extends AppManager {
  static registryName = 'RFQAppManager';
  rfqId = null;
  options = [];

  @observable assignRFQToManufacturerFormStore = null;
  @observable assignedManufacturerTarget = null;
  @observable conversionProgress = '';
  @observable conversionSteps = '';
  @observable shippingAddressFormTarget = {};
  @observable updateAddressFormStore = null;

  @observable addressRefs = {}; // for rfq page

  formLoaded = false;
  isFirstRfqLoad = true;

  constructor(parent, { rfqId = null, viewerControl = null }, options) {
    super(parent, null, options);

    this.viewerControl = viewerControl;

    this.rfqId = ensureIsGraphqlId(rfqId, 'RFQ');

    this.AppRef('rfqApp', RFQApp);
    this.AppRef('contextApp', ProjectContextApp);

    this.qualificationFormAppManager = new QualificationFormAppManager(this);

    // this.InitRFQAssignerElements();

    // attach quotes queryManager here or create using useAppManager?
    this.variables = new Variables({
      id: () => this.rfqId,
    });
    this.variables.onChange(['id'], this.LoadRfqQueryWithConversion);

    /*
    TODO: re-initialise queries on switching Portals.
      */
  }

  @action LoadRfqQueryWithConversion = () => {
    this.LoadRfqQuery(true);
  };

  @action LoadRfqQuery = allowConversion => {
    const variables = new Variables({
      id: () => this.variables.id,
      conversionSpec: allowConversion ? GLTF_VERSION || 'latest' : null,
    });
    const q = new RFQQuery(this, {
      queryKey: 'rfq',
      variables,
    });
    q.process(rfq => RFQModel.Load(rfq), this.RfqUpdate);
    q.process(
      context => (context ? ProjectContextModel.Load(context) : null),
      this.UpdateContext,
      { subPath: 'intermediatorSession.shareFile.context' },
    );
    q.query();
  };

  @action UpdateContext = contextModel => {
    if (!contextModel) return;
    this.contextApp = contextModel;
  };

  @computed get loading() {
    return this.queries.get('rfq')?.loading;
  }

  @computed get viewerFile() {
    const { signedInProfile } = clientStore;
    const { firstFile, revision } = this.rfqApp.intermediatorSession.shareFile;
    const { body } = this.rfqApp.jobSpec.build;
    let file = null;
    let partId = null;
    if (revision && revision.file) {
      // - this is the standard case, every RFQ is a shared file, and every shared
      // file is a versioned repository, we use the head revision of the shared file
      // as the file that we want to pass to the viewer

      // eslint-disable-next-line prefer-destructuring
      file = revision.file;
    } else if (
      signedInProfile?.user &&
      body &&
      (body.srcFile?.creator?.id === signedInProfile.user.id ||
        body.srcFile?.creator?.username === '@tmpuser')
    ) {
      // - when there is no revision, and there is a body with a srcFile in the build,
      // then this rfq is for an extracted part, and if the creator of the srcFile is
      // the user that is currently logged in, then we can use the srcFile and partId
      // as fallback for the viewer
      // - in practice this means a user will extract a part, and while the part is not
      // extracted+converted yet, he will simply see the assembly with the part open in
      // a viewer tab instead
      // - when the creator is tmpuser then the user was not logged in when he uploaded
      // the assembly and then logged in, so treat this the same is if the user was the
      // creator
      const { cadId, srcFile } = body;
      partId = cadId;
      file = srcFile;
    } else {
      // - when we do not have a revision, and do not own the srcFile, then we are
      // probably an intermediator looking at a rfq that has been recently created,
      // or is not working correctly, either way we can use the firstFile as fallback,
      // if it has no conversion the rfqAppManager should have subscribed to the gltf
      // conversion subscription
      file = firstFile;
    }

    return {
      file,
      partId,
    };
  }

  @action RfqUpdate = rfqModel => {
    if (!rfqModel) return;
    this.rfqApp = rfqModel;
    const { shareFile } = this.rfqApp.intermediatorSession;
    const { firstFile, revision } = shareFile;

    if (revision && revision.file) {
      // - standard case when creating a new RFQ should be there is shared file that
      // has a revision, and if that revision has no gltf conversion we need to subscribe
      // here
      const { conversionGltfjson } = revision.file;
      if (!conversionGltfjson || conversionGltfjson.status === 'PENDING') {
        // - if the conversion is pending then we need to subscribe, if it is anything other
        // then that it is ok to leave this conditional without subscribing at all, then it
        // is either sucessful or failed, and both are valid end states
        this.SubscribeConversionProgress(shareFile, revision.file);
      }
    } else if (
      firstFile &&
      (!firstFile.conversionGltfjson ||
        firstFile.conversionGltfjson.status === 'PENDING')
    ) {
      // - if we extract a part, then there is no revision, we fallback to the firstFile instead
      // (which is just the file that was first uploaded to the shared file, it is an alternative
      // to using the (head) revision)
      // - we only subscribe if it is pending, same as above, if its successful or failed already
      // we don't need to do anything
      this.SubscribeConversionProgress(shareFile, firstFile);
    }

    if (
      !this.conversionSubscription &&
      (!firstFile?.conversionGltfjson ||
        firstFile.conversionGltfjson.status === 'PENDING')
    ) {
      // - when there is no file, no conversion, and no subscription, something must
      // be wrong, as a last attempt we popup an error and suggest reloading the page
      Alert.error(
        'Sorry, we could not update the viewer with the correct file. You can try to reload the page to fix this.',
      );
    }

    this.setupAddressFormUpdater();

    this.qualificationFormAppManager.loadQualityFormDataFromRFQApp();
    if (this.isFirstRfqLoad) {
      // For Incomplete RFQs, if they are only incomplete because we are missing the address
      // then show the adress modal
      if (
        this.rfqApp.state === 'INCOMPLETE' &&
        !this.rfqApp.address &&
        this.rfqApp.fileRequirements.length === 0 &&
        !clientStore.hasRole('INTERMEDIATOR')
      ) {
        // we need to timeout, not only for user experience, but also because we need
        // to wait a react render cycle for the address form to be construct based off
        // setupAddressFormUpdater() that we just called and would not be ready if this was
        // executed immediately synchronously.
        setTimeout(() => {
          const description =
            'Please provide a delivery address so that we can include shipping costs in your final quote.';

          this.BeginChangeAddress(description);
        }, 3000);
      }

      this.qualificationFormAppManager.maybeShowQualityForm();
    }
    this.isFirstRfqLoad = false;
  };

  @action setupAddressFormUpdater() {
    this.shippingAddressFormTarget.address = this.rfqApp.address;

    addressConfigForRFQPage.fields.address.addressSpaceId =
      this.rfqApp.customerSpace.id;

    if (!this.updateAddressFormStore) {
      this.updateAddressFormStore = new FormStore(
        this.shippingAddressFormTarget,
        addressConfigForRFQPage,
        'addressForm',
      );

      if (this.addressFormUpdaterDisposer) this.addressFormUpdaterDisposer();
      this.addressFormUpdaterDisposer = autorun(
        () => {
          if (
            this.shippingAddressFormTarget.address?.id !==
            this.rfqApp.address?.id
          ) {
            this.UpdateShippingAddress(this.shippingAddressFormTarget.address);
          }
        },
        {
          fireImmediately: false,
        },
      );
      this.disposers.push(this.addressFormUpdaterDisposer);
    }
  }

  @action UpdateRFQState = newState => {
    const updateInput = new Input({
      input: {
        id: this.rfqApp.id,
        state: newState,
      },
    });

    const onUpdateRequestForQuoteStateError = () => {
      Alert.error('Sorry, there was an error changing the RFQ State.');
    };

    const updateRequestForQuoteStateMutation =
      new UpdateRequestForQuoteStateMutation(updateInput, null, {}, true);
    updateRequestForQuoteStateMutation.setCallbacks({
      onSysErr: onUpdateRequestForQuoteStateError,
      onUserErr: onUpdateRequestForQuoteStateError,
      // onSuccess:() => { },
    });
    updateRequestForQuoteStateMutation.mutate();
  };

  @action SubscribeConversionProgress(sharedFile, file) {
    // should be inside FilAppManager?
    if (this.conversionSubscription) {
      this.conversionSubscription.CompleteSubscription();
    }

    const variables = {
      subscribeFileProcessInput: {
        fileId: file.id,
        conversionType: 'gltf',
        contextId: sharedFile.context.id,
      },
    };

    this.conversionSubscription = new ConversionProgressSubscription(this, {
      variables,
    });

    this.conversionSubscription.process(this.handleConversionProgress);
    this.conversionSubscription.StartSubscription();
  }

  @action handleConversionProgress = result => {
    if (!result) {
      return;
    }

    if (this.rfqApp.intermediatorSession.revision) {
      // - I wrapped these into a check since I want this function to able to
      // handle conversions for shared files which have no revision yet, I assume
      // some other component relies on these values
      // - all other code should probably fallback to using the firstFile as well,
      // this which is what this code does and why it is neccessary for this
      // function to handle cases where this.rfqApp.intermediatorSession.revision
      // is null
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.status =
        result.status;
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.reason =
        result.reason;
      this.rfqApp.intermediatorSession.revision.file.conversionGltfjson.errorCode =
        result.errorCode;
    }

    if (result.progress !== '') {
      this.conversionProgress = result.progress;
    }
    if (result.steps !== '') {
      this.conversionSteps = result.steps;
    }

    if (
      result.status === 'SUCCESS' ||
      result.status === 'ERROR' ||
      result.status === 'PDF'
    ) {
      this.conversionSubscription?.CompleteSubscription();
      this.conversionSubscription = null;
      this.LoadRfqQuery();
    }
  };

  @action uploadFiles = (
    files,
    uploadCallback,
    cancelledCallback,
    options = { immadiate: false },
  ) => {
    const { immediate } = options;

    files.forEach(file => {
      const fileManager = new FileAppManager(null, {});
      if (immediate) {
        clientStore.uploader.FastTrackFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      } else {
        clientStore.uploader.QueueFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      }
    });
  };

  @action AddSupportingFiles = files => {
    this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [...files];
    const uploadedSupportingFileIds = [];

    const updateRFQ = () => {
      if (uploadedSupportingFileIds.length === 0) return;
      const updateInput = new Input({
        input: {
          requestForQuoteId: this.rfqId,
          jobSpec: {
            build: {
              supportingFiles: {
                toAdd: uploadedSupportingFileIds,
              },
            },
          },
        },
      });

      const onAddingSupportingFilesFinished = () => {
        this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [];
      };

      const onAddingSupportingFilesSuccess = updateRequestForQuote => {
        // We need to manually update these because otherwise there's a bit of time where the files being added
        // disappear (this mutation where they are uploaded finished) but the system has not yet finished querying
        // the whole state.
        const updatedSupportingFiles =
          updateRequestForQuote.jobSpec.build.supportingFiles;
        this.rfqApp.jobSpec.build.SetSupportingFiles(updatedSupportingFiles);

        this.rfqApp.fileRequirements = updateRequestForQuote.fileRequirements;

        onAddingSupportingFilesFinished();
      };

      const onError = () => {
        Alert.error(
          'Apologies, there was an error uploading your supporting file - please try again',
        );
        onAddingSupportingFilesFinished();
      };

      const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: onError,
        onUserErr: onError,
        onSuccess: onAddingSupportingFilesSuccess,
      });
      rfqMutation.mutate();
    };

    let uploadsFinished = 0;
    const uploadCallback = uploadedFile => {
      uploadsFinished += 1;
      uploadedSupportingFileIds.push(uploadedFile.id);
      if (uploadsFinished === files.length) updateRFQ();
    };
    const cancelledCallback = cancelledFile => {
      uploadsFinished += 1;
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(cancelledFile);
      if (uploadsFinished === files.length) updateRFQ();
    };

    this.uploadFiles(files, uploadCallback, cancelledCallback);
  };

  @action RemoveSupportingFile = (supportingFileId, callback) => {
    const updateInput = new Input({
      input: {
        requestForQuoteId: this.rfqId,
        jobSpec: {
          build: {
            supportingFiles: {
              toDelete: [supportingFileId],
            },
          },
        },
      },
    });

    const onError = () => {
      Alert.error(
        'Apologies, there was an error removing your supporting file - please try again',
      );
      callback?.(false);
    };

    const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
    rfqMutation.setCallbacks({
      onSysErr: onError,
      onUserErr: onError,
      onSuccess: () => {
        callback?.(true);
      },
    });
    rfqMutation.mutate();
  };

  @action UpdatePrimaryFile = localFile => {
    const updateRFQ = uploadedFile => {
      const updateInput = new Input({
        input: {
          requestForQuoteId: this.rfqId,
          jobSpec: {
            build: {
              fileId: uploadedFile.id,
            },
          },
        },
      });

      const currentRevisionFileId =
        this.rfqApp?.intermediatorSession?.shareFile?.revision?.file?.id;

      const disposer = when(
        () =>
          this.rfqApp?.intermediatorSession?.shareFile?.revision?.file?.id !==
          currentRevisionFileId,
        () => {
          // - this previously called LoadCadFile, but then we don't know if the
          // conversion was successful, and LoadCadFile won't subscribe and wait
          // for the gltf conversion
          // - so instead I changed it so it would just reload the RFQ, which is
          // a bit ugly atm but should wait and load the new gltf correctly
          this.LoadRfqQuery();
        },
      );
      this.disposers.push(disposer);

      const onUpdatePrimaryFileFinished = () => {};

      const onUpdatePrimaryFileSuccess = () => {
        // Calling this.LoadRfqQuery(); is unnecessary because Apollo cache will
        // launch this query on its own, because the mutation response will partially
        // update the cache results for the original loadRFQQuery and to prevent
        // inconsistencies Apollo will requery.
        // this.LoadRfqQuery();

        onUpdatePrimaryFileFinished();
      };

      const onError = () => {
        disposer();
        Alert.error(
          'Apologies, there was an error uploading your file - please try again',
        );
        onUpdatePrimaryFileFinished();
      };

      const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: onError,
        onUserErr: onError,
        onSuccess: onUpdatePrimaryFileSuccess,
      });
      rfqMutation.mutate();
    };
    this.uploadFiles([localFile], updateRFQ);
  };

  @action UpdateShippingAddress = address => {
    if (!address || !address.id) return;
    const updateInput = new Input({
      input: {
        requestForQuoteId: this.rfqId,
        addressId: address.id,
      },
    });

    const onUpdateShippingAddressFinished = () => {};

    const onUpdateShippingAddressSuccess = () => {
      this.rfqApp.address = address;
      onUpdateShippingAddressFinished();
    };

    const onError = () => {
      Alert.error('Apologies, there was an error updating your address');
      onUpdateShippingAddressFinished();
    };

    const rfqMutation = new UpdateRFQMutation(updateInput, null, {}, true);
    rfqMutation.setCallbacks({
      onSysErr: onError,
      onUserErr: onError,
      onSuccess: onUpdateShippingAddressSuccess,
    });
    rfqMutation.mutate();
  };

  @action BeginChangeAddress = (description = '') => {
    // the value of this.addressRefs is setup dynamically on
    // rendering the addresses field component
    if (this.addressRefs?.address) {
      this.addressRefs.address.manager.BeginChangeAddress(description);
    }
  };
}
